import { TimePicker, Switch, Button, notification, Form, Tag } from 'antd';
import { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getSelectedAgent, getSelectedAgentId } from '@/store/account/selector';
import momentTimeZone from 'moment-timezone';
import { useMutation } from '@tanstack/react-query';
import { updateUserTimeZone } from '@/api/logs';
import { useAppDispatch } from '@/store/hooks/useAppDispatch';
import { setUserTimeZone } from '@/store/account/slice';
import { useLocalTimezone } from 'src/common/hooks/useLocalTimezone';
import { TWorkflowAvailabilities } from '@/types/TWorkflowAvailabilities';
import { closeModal } from '@/store/modal/slice';

interface FormValues {
	availability: TWorkflowAvailabilities;
	configEnabled: boolean;
	fallbackMessage: string;
}

export const SetupWorkflowAvailability: React.FC<{
	mutation: any;
	workflowData: any;
	scenarioIndex: number;
}> = ({ mutation, workflowData, scenarioIndex }) => {
	const format = 'HH:mm';
	const [form] = Form.useForm<FormValues>();
	const dispatch = useAppDispatch();
	const selectedAgent = useAppSelector(getSelectedAgent);
	const selectedAgentId = useAppSelector(getSelectedAgentId);
	const { timeZone } = useLocalTimezone(selectedAgent?.time_zone);
	const [isLoading, setIsLoading] = useState(false);
	const [isTouched, setIsTouched] = useState(false);

	const updateTimeZoneMutation = useMutation({
		mutationKey: ['updateTimeZone'],
		mutationFn: updateUserTimeZone,
		onSuccess(_, values) {
			dispatch(setUserTimeZone({ timeZone: values.timeZone }));
		},
	});

	const defaultAvailability: TWorkflowAvailabilities = {
		Monday: { status: true, timings: ['09:00', '17:00'] },
		Tuesday: { status: true, timings: ['09:00', '17:00'] },
		Wednesday: { status: true, timings: ['09:00', '17:00'] },
		Thursday: { status: true, timings: ['09:00', '17:00'] },
		Friday: { status: true, timings: ['09:00', '17:00'] },
		Saturday: { status: false, timings: ['09:00', '17:00'] },
		Sunday: { status: false, timings: ['09:00', '17:00'] },
	};

	const [availability, setAvailability] = useState<TWorkflowAvailabilities>(
		workflowData?.availability || defaultAvailability,
	);

	const initialValues: FormValues = {
		availability: workflowData?.availability || defaultAvailability,
		configEnabled: workflowData?.configEnabled ?? false,
		fallbackMessage: workflowData?.fallbackMessage || '',
	};

	useEffect(() => {
		form.setFieldsValue(initialValues);
	}, [workflowData, form]);

	const handleValuesChange = () => {
		setIsTouched(true);
	};

	const getTimeComponents = (time: any) => {
		return {
			hour: dayjs(time).hour().toString().padStart(2, '0'),
			minute: dayjs(time).minute().toString().padStart(2, '0'),
		};
	};

	const forceEnableConfig = () => {
		if (!form.getFieldValue('configEnabled')) {
			form.setFieldsValue({ configEnabled: true });
		}
	};

	const handleTimeChange = (day: keyof TWorkflowAvailabilities, times: any) => {
		if (!times) {
			return;
		}

		const { hour: startHour, minute: startMinute } = getTimeComponents(times[0]);
		const { hour: endHour, minute: endMinute } = getTimeComponents(times[1]);

		const currentAvailability = availability;
		const updatedAvailability = {
			...currentAvailability,
			[day]: {
				status: currentAvailability[day].status,
				timings: [`${startHour}:${startMinute}`, `${endHour}:${endMinute}`],
			},
		};

		setAvailability(updatedAvailability);
		setIsTouched(true);
	};

	const handleDayStatus = (status: boolean, day: keyof TWorkflowAvailabilities) => {
		const currentAvailability = availability;
		const updatedAvailability = {
			...currentAvailability,
			[day]: {
				status,
				timings: currentAvailability[day].timings,
			},
		};

		setAvailability(updatedAvailability);
		setIsTouched(true);
	};

	const handleFormSubmit = async (values: FormValues) => {
		setIsLoading(true);
		try {
			if (!selectedAgent?.time_zone || selectedAgent.time_zone === 'na') {
				const localTimezone = momentTimeZone.tz.guess();
				if (localTimezone !== selectedAgent?.time_zone) {
					await updateTimeZoneMutation.mutateAsync({
						timeZone: localTimezone,
						agentId: selectedAgentId as string,
					});
				}
			}

			const trimmedMessage = values.fallbackMessage.trim();
			if (!trimmedMessage) {
				notification.error({
					message: 'Error',
					description: 'Please enter a prompt for AI during inactive hours',
				});
				return;
			}

			await mutation(availability, scenarioIndex, trimmedMessage, values.configEnabled);
			dispatch(closeModal());
		} catch (error) {
			console.error('Error saving availability:', error);
		} finally {
			setIsLoading(false);
		}
	};

	const validateFallbackMessageLength = (_: any, value: string) => {
		if (value && value.length > 200) {
			return Promise.reject(new Error('Prompt must be 200 characters or fewer'));
		}
		return Promise.resolve();
	};

	return (
		<Form
			form={form}
			onFinish={handleFormSubmit}
			onValuesChange={handleValuesChange}
			initialValues={initialValues}
			layout="vertical"
			className="flex flex-col"
		>
			<div className="mb-4 flex justify-between items-baseline">
				<p className="text-sm text-gray-600 flex items-center items-baseline">
					Please set workflow availability hours for each day of the week.
					<br />
					<span>
						{'  '}Timezone:
						<span className="font-semibold text-gray-500 ml-2">{timeZone}</span>
					</span>
				</p>
				<div className="flex items-center gap-2 items-baseline">
					<Form.Item
						noStyle
						shouldUpdate={(prevValues, currentValues) =>
							prevValues.configEnabled !== currentValues.configEnabled
						}
					>
						{({ getFieldValue }) =>
							!getFieldValue('configEnabled') ? (
								<Tag color="default" className="text-xs">
									Disabled
								</Tag>
							) : (
								<span className="text-sm text-gray-500">Enabled</span>
							)
						}
					</Form.Item>
					<Form.Item name="configEnabled" valuePropName="checked" className="mb-0">
						<Switch />
					</Form.Item>
				</div>
			</div>

			<Form.Item
				noStyle
				shouldUpdate={(prevValues, currentValues) =>
					prevValues.configEnabled !== currentValues.configEnabled
				}
			>
				{({ getFieldValue }) => {
					const currentConfigEnabled = getFieldValue('configEnabled');

					return (
						<div
							onClick={forceEnableConfig}
							className={`mb-2 p-4 bg-white border rounded-2xl shadow-sm ${
								!currentConfigEnabled
									? 'border-gray-200 bg-gray-50 text-gray-400 cursor-pointer'
									: 'border-indigo-200 hover:scale-[101%] hover:transition'
							}`}
						>
							{(Object.keys(availability) as (keyof TWorkflowAvailabilities)[]).map((day, key) => (
								<div
									key={key}
									className={`flex justify-between items-center mb-3 px-2 py-1 transition ${
										!currentConfigEnabled ? '' : 'hover:bg-pink-50 rounded-xl'
									}`}
								>
									<div className="flex items-center gap-3 w-1/3">
										<Switch
											className={`${availability[day].status ? 'bg-black' : 'bg-rose-400'} ${
												!currentConfigEnabled ? 'opacity-50 cursor-pointer' : ''
											}`}
											data-testid="day-switch"
											checked={availability[day].status}
											onChange={(value) => {
												handleDayStatus(value, day);
											}}
										/>
										<div
											className={`text-sm font-semibold capitalize ${
												!currentConfigEnabled ? 'text-gray-400' : 'text-gray-800'
											}`}
										>
											{day}
										</div>
									</div>
									<div className="flex">
										{availability[day].status ? (
											<TimePicker.RangePicker
												allowClear={false}
												format={format}
												onChange={(times) => {
													handleTimeChange(day, times);
												}}
												value={[
													dayjs(availability[day].timings[0], format),
													dayjs(availability[day].timings[1], format),
												]}
												className={`rounded-xl border transition ${
													!currentConfigEnabled
														? 'border-gray-200 bg-gray-50 text-gray-400 ant-picker-disabled opacity-50 cursor-pointer'
														: 'border-indigo-200 focus:border-pink-400'
												}`}
												style={{ width: '100%', maxWidth: '200px' }}
											/>
										) : (
											<span className="text-xs text-gray-400 italic">Off for the day</span>
										)}
									</div>
								</div>
							))}
						</div>
					);
				}}
			</Form.Item>

			<Form.Item
				noStyle
				shouldUpdate={(prevValues, currentValues) =>
					prevValues.configEnabled !== currentValues.configEnabled
				}
			>
				{({ getFieldValue }) => {
					const currentConfigEnabled = getFieldValue('configEnabled');

					return (
						<Form.Item
							name="fallbackMessage"
							label={
								<span className={!currentConfigEnabled ? 'text-gray-400' : 'text-gray-700'}>
									Prompt for AI during inactive hours <span className="text-red-500">*</span>
								</span>
							}
							rules={[
								{ required: true, message: 'Please enter a prompt for AI during inactive hours' },
								{ whitespace: true, message: 'Prompt cannot be empty' },
								{ validator: validateFallbackMessageLength },
							]}
							className="mb-4"
						>
							<input
								type="text"
								placeholder="e.g. Tell User that 'We are currently closed. Please call again during working hours'"
								readOnly={!currentConfigEnabled}
								onClick={forceEnableConfig}
								className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 placeholder:text-gray-400 ${
									!currentConfigEnabled
										? 'border-gray-200 bg-gray-50 text-gray-400 cursor-pointer'
										: 'border-indigo-200 focus:ring-indigo-300 focus:border-indigo-400'
								}`}
							/>
						</Form.Item>
					);
				}}
			</Form.Item>

			{isTouched && (
				<div className="flex justify-end mt-4">
					<Button
						htmlType="submit"
						loading={isLoading}
						className="transform duration-150 ease-in-out active:scale-95 px-4 py-4 rounded bg-blue-500 text-white hover:bg-blue-600"
					>
						Save
					</Button>
					<Button
						onClick={() => dispatch(closeModal())}
						disabled={isLoading}
						className="btn-secondary bg-blue-500 hover:bg-blue-600 text-white px-4 py-4 rounded ml-4"
					>
						Cancel
					</Button>
				</div>
			)}
		</Form>
	);
};
